import type { CellClickedEvent } from '@ag-grid-community/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, type OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BARE_DOMAIN_ERROR,
  BARE_DOMAIN_REGEX,
  type CustomerAssetSyncStatus,
  type CustomerDetail,
  type CustomerDomainDetail,
  type CustomerOnboardingSummary,
  type CustomerUserSummary,
  type DwdCheckResult,
  type DwdScopeCheckResult,
  EMAIL_ERROR,
  ExecutionStatus,
  extractServerMessage,
  isValidEmail,
  ONBOARDING_STEP_LABELS,
  type OnboardingStepStatus,
} from '@cdw-ae/common-data';
import { MatDialog } from '@cdw-ae/common-frontend-materials';
import { CdwButtonModule } from '@cdw-ae/common-ui-components-button';
import {
  CdwConfirmationDialogComponent,
  type ConfirmationDialogResult,
} from '@cdw-ae/common-ui-components-dialog';
import { CdwDividerModule } from '@cdw-ae/common-ui-components-divider';
import { CdwIconModule } from '@cdw-ae/common-ui-components-icon';
import {
  type CdwColDef,
  CdwSsrmGridComponent,
  type CdwValueFormatterParams,
} from '@cdw-ae/common-ui-components-search-grid';
import type { FilterBarField } from '@cdw-ae/filter-bar-interfaces';
import {
  ContainsMatcher,
  IsMatcher,
  IsNotMatcher,
  StartsWithMatcher,
} from '@cdw-ae/filter-bar-matcher-configs';
import { FilterBarSelectionMatchersModule } from '@cdw-ae/filter-bar-selection-matchers';
import { FilterBarTextMatchersModule } from '@cdw-ae/filter-bar-text-matchers';
import { take } from 'rxjs/operators';
import { LicenseCheckPanelComponent } from '../../../../common/components/license-check-panel/license-check-panel.component';
import type { ChipTone } from '../../../../common/services/chip-tone';
import { CustomerUsersDataService } from '../../../../common/services/customer-users-data/customer-users-data.service';
import {
  dwdStatusLabel,
  dwdStatusTone,
} from '../../../../common/services/platform-customer/dwd-status';
import {
  onboardingStatusLabel,
  onboardingStatusTone,
} from '../../../../common/services/platform-customer/onboarding-status';
import { PlatformCustomerService } from '../../../../common/services/platform-customer/platform-customer.service';

/**
 * 404 copy for the domain actions. The server's own message is discarded by the
 * global exception filter outside `local`, so the meaning has to be supplied
 * here — and for these three actions a 404 always means the same thing: the
 * row id came from this page's state, so that state is stale.
 */
const STALE_DOMAIN =
  'That domain is no longer on this customer — someone may have changed it in another tab. Refresh the page to see the current list.';

/**
 * Read-only customer detail: a universal section + a section per enrolled
 * provider (with that provider's domains). Reached via the eye icon on the list.
 */
@Component({
  selector: 'cdw-customer-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatProgressSpinnerModule,
    CdwButtonModule,
    CdwDividerModule,
    CdwIconModule,
    CdwSsrmGridComponent,
    FilterBarTextMatchersModule,
    FilterBarSelectionMatchersModule,
    LicenseCheckPanelComponent,
  ],
  // Component-scoped so its per-tenant customerId can't go stale between views.
  providers: [CustomerUsersDataService],
  templateUrl: './customer-detail.component.html',
  styleUrl: './customer-detail.component.scss',
})
export class CustomerDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly customerService = inject(PlatformCustomerService);
  private readonly dialog = inject(MatDialog);
  readonly usersDataService = inject(CustomerUsersDataService);

  /** For refreshing the grid after a tour reset (EDUS-2852). */
  @ViewChild('usersGrid') usersGrid?: CdwSsrmGridComponent<CustomerUserSummary>;

  readonly statusLabel = dwdStatusLabel;
  readonly statusTone = dwdStatusTone;
  readonly onboardingLabel = onboardingStatusLabel;
  readonly onboardingTone = onboardingStatusTone;
  readonly stepLabels = ONBOARDING_STEP_LABELS;

  isLoading = true;
  errorMessage: string | null = null;
  notFound = false;
  customer: CustomerDetail | null = null;

  onboarding: CustomerOnboardingSummary | null = null;
  onboardingError = false;
  refreshingOnboarding = false;

  assetSync: CustomerAssetSyncStatus | null = null;
  assetSyncError = false;
  refreshingAssetSync = false;

  // Authorized domains (EDUS-2864). `domainBusyId` marks the row mid-request so
  // only that row's controls disable, rather than the whole list.
  domainInput = '';
  addingDomain = false;
  domainBusyId: number | null = null;
  domainError: string | null = null;

  // Users grid (SSRM): server paginate/filter/sort via CustomerUsersDataService.
  // Left untyped (CdwColDef<unknown>) to match CdwValueFormatterParams, which
  // has no generic of its own — parameterising the array on CustomerUserSummary
  // makes every existing valueFormatter here fail to assign.
  readonly userColumnDefs: CdwColDef[] = [
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 160 },
    { field: 'email', headerName: 'Email', flex: 1, minWidth: 200 },
    {
      field: 'createdAt',
      headerName: 'First Seen',
      width: 150,
      valueFormatter: (params: CdwValueFormatterParams): string =>
        params.value
          ? new Date(params.value as string).toLocaleDateString()
          : '',
    },
    {
      // EDUS-2852. "Not yet" rather than blank: null is the meaningful state
      // here (it's what routes them to /tour), so it needs to read as a value
      // and not as missing data.
      field: 'firstLoginAt',
      headerName: 'Tour Seen',
      width: 150,
      valueFormatter: (params: CdwValueFormatterParams): string =>
        params.value
          ? new Date(params.value as string).toLocaleDateString()
          : 'Not yet',
    },
    {
      // A clickable text cell rather than a rendered button: an AG Grid cell
      // renderer means an extra Angular component and its wiring, which is a
      // lot of machinery for one QA affordance in an internal console.
      colId: 'resetTour',
      headerName: 'Reset Tour',
      width: 120,
      sortable: false,
      valueGetter: (params) =>
        (params.data as CustomerUserSummary | undefined)?.firstLoginAt
          ? 'Reset'
          : '',
      cellClass: 'cursor-pointer font-bold text-blue-700',
    },
  ];

  readonly userFilterBarSettings: FilterBarField[] = [
    {
      displayName: 'Name',
      name: 'name',
      singleUse: true,
      fieldMatchers: [
        { ...ContainsMatcher, suggestMatcher: true },
        StartsWithMatcher,
      ],
    },
    {
      displayName: 'Email',
      name: 'email',
      singleUse: true,
      fieldMatchers: [
        { ...ContainsMatcher, suggestMatcher: true },
        StartsWithMatcher,
      ],
    },
    {
      // EDUS-2852. Answers "who hasn't been oriented yet" — the only question
      // worth asking of this column, and the cohort you'd chase or reset.
      displayName: 'Tour Seen',
      name: 'first_login_at',
      singleUse: true,
      fieldMatchers: [
        {
          // `is` + 'missing' compiles to IS NULL server-side. It is FIRST and
          // suggested deliberately: the filter bar only ever offers a field's
          // first matcher or one flagged suggestMatcher, so a second matcher
          // would be inert config (see the DWD field on the customers list,
          // EDUS-2846). The un-oriented cohort is the one worth reaching.
          ...IsMatcher,
          suggestMatcher: true,
          editorOptions: {
            // Relabelled from the matcher's default "Missing" so the chip reads
            // "Tour Seen: Not yet", matching the grid cell's own wording. The
            // `name` must stay 'missing' — that's what the server maps to NULL.
            selectionOptions: [{ name: 'missing', displayName: 'Not yet' }],
          },
          options: {
            chipFormatter: (): string => ': Not yet',
          },
        },
        // The inverse (IS NOT NULL, i.e. already oriented) — correct but
        // CURRENTLY UNREACHABLE for the same reason as above. Kept so it starts
        // working the moment EDUS-2846 closes; until then, sort the column
        // instead (Postgres puts NULLs first on DESC). Its options are supplied
        // here because the shared IsNotMatcher ships with an EMPTY
        // selectionOptions list — inheriting it would give an editor with
        // nothing to pick the day it does become reachable.
        {
          ...IsNotMatcher,
          editorOptions: {
            selectionOptions: [{ name: 'missing', displayName: 'Not yet' }],
          },
          options: {
            chipFormatter: (): string => ': Seen',
          },
        },
      ],
    },
  ] as FilterBarField[];

  /** Id of the user whose tour reset is in flight (EDUS-2852), else null. */
  resettingTourFor: number | null = null;
  resetTourError: string | null = null;

  checking = false;
  checkResult: DwdCheckResult | null = null;

  // Per-scope breakdown (EDUS-2769). Separate from `checking`/`checkResult`
  // above because it's a different call that persists nothing — running it must
  // not disturb the stored status the rest of this section displays.
  scopeChecking = false;
  scopeResult: DwdScopeCheckResult | null = null;
  scopeError: string | null = null;
  correcting = false;
  correctionError: string | null = null;
  resetting = false;
  resetError: string | null = null;
  releasingLock = false;
  releaseLockError: string | null = null;
  completingSync = false;
  completeSyncError: string | null = null;
  delegateInput = '';
  updatingDelegate = false;
  delegateError: string | null = null;

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    const id = Number(idParam);
    if (!idParam || Number.isNaN(id)) {
      this.isLoading = false;
      this.errorMessage = 'Invalid customer id.';
      return;
    }

    this.customerService.getCustomer(id).subscribe({
      next: (customer) => {
        this.customer = customer;
        this.delegateInput = customer.providers[0]?.delegateEmail ?? '';
        this.isLoading = false;
      },
      error: (err: HttpErrorResponse) => {
        this.isLoading = false;
        if (err.status === 404) {
          this.notFound = true;
        } else {
          this.errorMessage = 'Failed to load the customer. Please try again.';
        }
      },
    });

    this.loadOnboarding(id);
    this.loadAssetSync(id);
    // The Users grid pulls its own pages via the SSRM data service.
    this.usersDataService.customerId = id;
  }

  /**
   * Asset Sync panel — the customer's full-asset-sync state (running + live
   * counts, or last-run status/time). One-shot fetch + a Refresh action, since a
   * platform admin can't live-bind another tenant's Firestore data.
   */
  loadAssetSync(id: number): void {
    this.assetSyncError = false;
    this.refreshingAssetSync = true;
    this.customerService.getAssetSyncStatus(id).subscribe({
      next: (status) => {
        this.assetSync = status;
        this.refreshingAssetSync = false;
      },
      error: () => {
        this.assetSyncError = true;
        this.refreshingAssetSync = false;
      },
    });
  }

  /**
   * Onboarding diagnostic panel — independent of the main detail load. Also the
   * panel's Refresh action, so support can re-poll a tenant's live progress
   * (e.g. watch a Step-4 sync flip to complete) without reloading the page.
   */
  loadOnboarding(id: number): void {
    this.onboardingError = false;
    this.refreshingOnboarding = true;
    this.customerService.getOnboardingSummary(id).subscribe({
      next: (summary) => {
        this.onboarding = summary;
        this.refreshingOnboarding = false;
      },
      error: () => {
        this.onboardingError = true;
        this.refreshingOnboarding = false;
      },
    });
  }

  /**
   * Support/QA: update the DWD delegate email. Invalidates the DWD status
   * server-side (it was for the old delegate); reflect that in place so the
   * chip shows "not checked" until the next login re-verifies.
   */
  updateDelegate(): void {
    const customer = this.customer;
    const delegate = this.delegateInput.trim();
    if (!(customer && delegate) || this.updatingDelegate) {
      return;
    }
    // Instant feedback for a malformed address, mirroring the wizard's Step 2
    // (which is why that screen "reads nicely" and this one didn't): the server
    // re-validates, but its DTO failure is unreadable by the time it reaches
    // us. `ValidationPipe` throws with `message` as an ARRAY, which
    // `HttpException` can't use as its top-level string, so Nest derives
    // "Bad Request Exception" — and in prod the filter drops the `details` that
    // hold the real text. Not sending the request is the only fix that works in
    // every environment.
    if (!isValidEmail(delegate)) {
      this.delegateError = EMAIL_ERROR;
      return;
    }

    this.updatingDelegate = true;
    this.delegateError = null;
    this.customerService.updateDelegate(customer.id, delegate).subscribe({
      next: (res) => {
        this.updatingDelegate = false;
        if (customer.providers[0]) {
          customer.providers[0].delegateEmail = res.delegateEmail;
        }
        customer.dwdCheckStatus = null;
        customer.lastDwdCheckAt = null;
        this.checkResult = null;
      },
      // EDUS-2864: this used to be a hardcoded "Failed to update the delegate."
      // regardless of why. That was survivable while the endpoint stored
      // whatever it was given and could only really fail on a 500 — but it now
      // enforces the wizard's rules, so the interesting cases (off-domain
      // address, malformed email) are exactly the ones that string hid. The
      // wizard shows the server's text for the identical rule; so does this.
      error: (err: HttpErrorResponse) => {
        this.updatingDelegate = false;
        this.delegateError = this.actionErrorMessage(
          err,
          'update the delegate',
          'This customer no longer exists — refresh the page.'
        );
      },
    });
  }

  /**
   * Add an authorized domain (EDUS-2864).
   *
   * Server messages are shown verbatim, matching the wizard's Step 2: every
   * rejection here is a specific, actionable business rule ("Public email
   * domains can't be added.", "…assigned to customer 42 (Springfield USD)."),
   * and flattening those to "please try again" would throw away the only
   * information support needs.
   */
  addDomain(): void {
    const customer = this.customer;
    const domain = this.domainInput.trim().toLowerCase();
    if (!(customer && domain) || this.addingDomain) {
      return;
    }
    // Same pre-check as the wizard's domain field, and for the same reason as
    // the delegate above — a malformed value would come back as an unreadable
    // "Bad Request Exception". Shared regex + wording, so the two screens
    // can't drift.
    if (!BARE_DOMAIN_REGEX.test(domain)) {
      this.domainError = BARE_DOMAIN_ERROR;
      return;
    }

    this.addingDomain = true;
    this.domainError = null;
    this.customerService.addDomain(customer.id, domain).subscribe({
      next: (added) => {
        this.addingDomain = false;
        this.domainInput = '';
        customer.providers[0]?.domains.push(added);
      },
      error: (err: HttpErrorResponse) => {
        this.addingDomain = false;
        this.domainError = this.actionErrorMessage(
          err,
          'add that domain',
          STALE_DOMAIN
        );
      },
    });
  }

  /**
   * Remove an authorized domain. The confirmation names the user count because
   * that is the part support can't see anywhere else — those accounts are left
   * in place, they just lose the domain-match route into this tenant.
   */
  confirmRemoveDomain(domain: CustomerDomainDetail): void {
    const customer = this.customer;
    if (!customer || this.domainBusyId !== null) {
      return;
    }
    const impact =
      domain.userCount > 0
        ? `${domain.userCount} provisioned user${
            domain.userCount === 1 ? '' : 's'
          } on this domain will lose access at their next sign-in. Their accounts are not deleted.`
        : 'No provisioned users are on this domain.';

    this.confirmDestructive({
      title: 'Remove domain?',
      message:
        `Remove ${domain.domainName} from ${customer.name}'s authorized domains? ` +
        `${impact} You can add it back later.`,
      confirmText: 'Remove domain',
    })
      .pipe(take(1))
      .subscribe((result: ConfirmationDialogResult | undefined) => {
        if (!result?.confirmed) {
          return;
        }
        this.domainBusyId = domain.id;
        this.domainError = null;
        this.customerService.removeDomain(customer.id, domain.id).subscribe({
          next: () => {
            this.domainBusyId = null;
            const provider = customer.providers[0];
            if (provider) {
              provider.domains = provider.domains.filter(
                (d) => d.id !== domain.id
              );
            }
          },
          error: (err: HttpErrorResponse) => {
            this.domainBusyId = null;
            this.domainError = this.actionErrorMessage(
              err,
              'remove that domain',
              STALE_DOMAIN
            );
          },
        });
      });
  }

  /**
   * Promote a secondary domain to primary. Reloads the detail afterwards rather
   * than patching in place: the swap moves the flag on TWO rows plus the
   * customer's own primaryDomain, and the licensing panel is bound to that
   * field, so a partial local update would leave the page inconsistent.
   */
  confirmMakePrimary(domain: CustomerDomainDetail): void {
    const customer = this.customer;
    if (!customer || this.domainBusyId !== null) {
      return;
    }
    this.confirmDestructive({
      title: 'Make primary domain?',
      message:
        `Make ${domain.domainName} the primary domain for ${customer.name}? ` +
        `${customer.primaryDomain ?? 'The current primary'} becomes a secondary ` +
        'domain and stays authorized — remove it separately if it should go. ' +
        'The licence check re-targets to the new primary.',
      confirmText: 'Make primary',
    })
      .pipe(take(1))
      .subscribe((result: ConfirmationDialogResult | undefined) => {
        if (!result?.confirmed) {
          return;
        }
        this.domainBusyId = domain.id;
        this.domainError = null;
        this.customerService
          .makePrimaryDomain(customer.id, domain.id)
          .subscribe({
            next: () => {
              this.domainBusyId = null;
              this.reloadCustomer(customer.id);
            },
            error: (err: HttpErrorResponse) => {
              this.domainBusyId = null;
              this.domainError = this.actionErrorMessage(
                err,
                'change the primary domain',
                STALE_DOMAIN
              );
            },
          });
      });
  }

  /**
   * Refetch the detail in place after a change that touches several fields at
   * once. Deliberately does NOT set `isLoading` — that would tear the whole
   * page down to a spinner for what is a one-row edit.
   */
  private reloadCustomer(id: number): void {
    this.customerService.getCustomer(id).subscribe({
      next: (customer) => {
        this.customer = customer;
      },
      error: () => {
        this.domainError =
          'The change was saved, but the page could not be refreshed. Reload to see the current state.';
      },
    });
  }

  /**
   * Turn a failed domain request into something support can act on.
   *
   * `action` is a verb phrase ("remove that domain") used only by the last two
   * branches, so every caller gets specific copy rather than one shared
   * "please try again".
   *
   * Order matters:
   * - **0** means no HTTP response at all (offline, blocked request) — there is
   *   no server message to read, and "try again" is genuinely the advice.
   * - **404 is handled here, not server-side, on purpose.** The global
   *   exception filter rewrites EVERY 404 body to "Resource not found" outside
   *   `local` (`global-exception.filter.ts`), so any message the endpoint
   *   writes is discarded in every deployed environment. The client knows the
   *   context the server's sanitized string lost — but *what* went missing
   *   differs per caller (a domain row vs the customer), so `notFound` is
   *   supplied by the caller rather than assumed here.
   * - Any other **4xx** is a deliberate rule violation whose text is the whole
   *   point ("…already assigned to customer 12 (Shelbyville)"). Pass it
   *   through verbatim, as the wizard does.
   * - **5xx** has no useful text to show.
   */
  private actionErrorMessage(
    err: HttpErrorResponse,
    action: string,
    notFound = 'That record no longer exists — refresh the page to see the current state.'
  ): string {
    if (err.status === 0) {
      return `Couldn’t reach the server to ${action}. Check your connection and try again.`;
    }
    if (err.status === 404) {
      return notFound;
    }

    const text = extractServerMessage(err.error);
    if (err.status >= 400 && err.status < 500 && text) {
      return text;
    }

    // The status is IN the copy on purpose. This is an internal support
    // console, not a customer surface: "please try again" with no code sent us
    // hunting through the backend for a message the backend was sending
    // correctly all along. A reader who can see `HTTP 500` diagnoses it
    // immediately, and it costs a support engineer nothing to ignore.
    const detail = text ? ` — ${text}` : '';
    return `Couldn’t ${action} (HTTP ${err.status})${detail}. Please try again, and quote this to engineering if it persists.`;
  }

  /**
   * The shared "are you sure?" for this page's support levers — same warning
   * treatment and sizing every time, so only the wording differs per action.
   */
  private confirmDestructive(copy: {
    title: string;
    message: string;
    confirmText: string;
  }) {
    return this.dialog
      .open(CdwConfirmationDialogComponent, {
        data: {
          ...copy,
          type: 'warning',
          layout: 'warning-box',
          cancelText: 'Cancel',
          confirmButtonColor: 'warn',
          hard: true,
        },
        width: '560px',
        maxWidth: '100%',
        disableClose: true,
        hasBackdrop: true,
      })
      .afterClosed();
  }

  /**
   * QA lever (EDUS-2852): clear one user's first-login stamp so their next
   * sign-in is treated as a first entry and lands on `/tour` again. Without it
   * the orientation flow is a one-shot per person and effectively untestable —
   * `resetOnboarding` is customer-scoped and deliberately never touches users.
   *
   * Fires off the "Reset Tour" cell only, and only for a user who has actually
   * been in; the cell is blank otherwise.
   */
  onUserCellClicked(event: CellClickedEvent): void {
    if (event.colDef.colId !== 'resetTour' || this.resettingTourFor !== null) {
      return;
    }
    const user = event.data as CustomerUserSummary | undefined;
    const customer = this.customer;
    // Nothing to reset for a user who has never been in — the cell is blank
    // for them, so this is just belt and braces.
    if (!user?.firstLoginAt) {
      return;
    }
    if (!customer) {
      return;
    }

    // Not confirmDestructive: nothing is destroyed and nobody is locked out —
    // the user just sees a welcome page once more. A warning dialog here would
    // cry wolf against the resets on this page that genuinely are dangerous.
    this.resettingTourFor = user.id;
    this.resetTourError = null;
    this.customerService.clearFirstLogin(customer.id, user.id).subscribe({
      next: () => {
        this.resettingTourFor = null;
        // Re-read from the server rather than patching the row in place: the
        // grid is server-side, so its cache is the only copy and a local edit
        // would silently disagree with the next page fetch. refreshLoadedBlocks
        // (not purge) — the same rows in the same order, one cell changed, so
        // there's no reason to throw the user back to page 0.
        this.usersGrid?.refreshLoadedBlocks();
      },
      error: () => {
        this.resettingTourFor = null;
        this.resetTourError =
          'Failed to reset the tour for that user. Please try again.';
      },
    });
  }

  /**
   * Support/QA reset (EDUS-2726): flips the customer back to the full
   * pre-onboarding state (uninitialized + DWD audit cleared). Big hammer —
   * the confirm dialog spells out the login consequences.
   */
  confirmResetOnboarding(): void {
    const customer = this.customer;
    if (!customer || this.resetting) {
      return;
    }
    this.confirmDestructive({
      title: 'Reset onboarding?',
      message:
        `Reset ${customer.name} to the pre-onboarding state? This clears its ` +
        'DWD verification progress and returns it to the start of onboarding: ' +
        'every user is routed into the wizard at next sign-in, and non-admin ' +
        'users are blocked until a super admin completes setup. No data is ' +
        'deleted — users, roles, domains, and the delegate are all kept.',
      confirmText: 'Reset onboarding',
    })
      .pipe(take(1))
      .subscribe((result: ConfirmationDialogResult | undefined) => {
        if (!result?.confirmed) {
          return;
        }
        this.resetting = true;
        this.resetError = null;
        this.customerService.resetOnboarding(customer.id).subscribe({
          next: (res) => {
            this.resetting = false;
            customer.onboardingStatus = res.onboardingStatus;
            customer.dwdCheckStatus = res.dwdCheckStatus;
            customer.lastDwdCheckAt = res.lastDwdCheckAt;
            this.checkResult = null;
            // The checklist below is derived server-side from the columns this
            // just cleared (and, since EDUS-2770, from Redis run state the reset
            // also drops). Patching the header fields alone would leave it
            // showing the pre-reset state until someone reloaded the page.
            this.loadOnboarding(customer.id);
          },
          error: () => {
            this.resetting = false;
            this.resetError = 'Failed to reset onboarding. Please try again.';
          },
        });
      });
  }

  /**
   * Force-release the tenant's onboarding session lock (US-12 support action).
   * The lock expires on its own after 30 minutes of inactivity; this is the
   * "don't make them wait" lever for an admin whose browser died mid-wizard.
   * Confirmed because it can boot a colleague who is genuinely mid-setup.
   */
  releaseOnboardingLock(customerId: number): void {
    if (this.releasingLock) {
      return;
    }
    const holder = this.onboarding?.activeSession;
    this.confirmDestructive({
      title: 'Release onboarding lock?',
      message:
        `${holder?.name || holder?.email || 'An administrator'} is currently ` +
        'configuring this tenant. Releasing the lock lets any super admin start ' +
        'configuring immediately — if they are still working, they will be ' +
        'blocked out on their next action. Saved configuration is not affected.',
      confirmText: 'Release lock',
    })
      .pipe(take(1))
      .subscribe((result: ConfirmationDialogResult | undefined) => {
        if (!result?.confirmed) {
          return;
        }
        this.releasingLock = true;
        this.releaseLockError = null;
        this.customerService.releaseOnboardingLock(customerId).subscribe({
          next: () => {
            this.releasingLock = false;
            // Re-poll so the panel reflects the now-free tenant.
            this.loadOnboarding(customerId);
          },
          error: () => {
            this.releasingLock = false;
            this.releaseLockError =
              'Failed to release the onboarding lock. Please try again.';
          },
        });
      });
  }

  /**
   * Force the Step-4 sync gate open (EDUS-2858) — support's only escape from a
   * tenant whose sync fails the same way every time.
   *
   * Confirmed, and worded to make the trade explicit: this admits the tenant to
   * the app over data we know to be incomplete, and unlike most support levers
   * it does NOT heal on its own — the nightly recurring sync runs the same
   * workflow and fails the same way, so the gap persists until the underlying
   * cause is fixed.
   */
  completeInitialSync(customerId: number): void {
    if (this.completingSync) {
      return;
    }
    this.confirmDestructive({
      title: 'Force the sync gate open?',
      message:
        'This tenant will be let into the app even though their last sync did ' +
        'not finish cleanly, so some of their data will be missing. It will ' +
        'NOT fix itself — the nightly sync runs the same job and will fail the ' +
        'same way. Only do this when retrying has already failed repeatedly.',
      confirmText: 'Force sync complete',
    })
      .pipe(take(1))
      .subscribe((result: ConfirmationDialogResult | undefined) => {
        if (!result?.confirmed) {
          return;
        }
        this.completingSync = true;
        this.completeSyncError = null;
        this.customerService.completeInitialSync(customerId).subscribe({
          next: () => {
            this.completingSync = false;
            // Re-poll so the checklist shows Sync Data as complete.
            this.loadOnboarding(customerId);
          },
          error: () => {
            this.completingSync = false;
            this.completeSyncError =
              'Failed to force the sync gate open. Please try again.';
          },
        });
      });
  }

  checkDwd(): void {
    if (!this.customer || this.checking) {
      return;
    }
    this.checking = true;
    this.checkResult = null;
    this.correctionError = null;
    this.customerService.checkDwd(this.customer.id).subscribe({
      next: (result) => {
        this.checking = false;
        this.checkResult = result;
        if (this.customer) {
          this.customer.dwdCheckStatus = result.status;
          this.customer.lastDwdCheckAt = result.checkedAt;
        }
      },
      error: () => {
        this.checking = false;
        this.checkResult = null;
        this.errorMessage = 'The delegation check could not be started.';
      },
    });
  }

  /**
   * Support diagnostic: which scopes are actually granted. Deliberately does
   * not update `customer.dwdCheckStatus` — the endpoint persists nothing, and
   * the Status field above must keep showing the real last verification.
   */
  checkScopes(): void {
    if (!this.customer || this.scopeChecking) {
      return;
    }
    this.scopeChecking = true;
    this.scopeResult = null;
    this.scopeError = null;
    this.customerService.checkDwdScopes(this.customer.id).subscribe({
      next: (result) => {
        this.scopeChecking = false;
        this.scopeResult = result;
      },
      error: (err: { status?: number }) => {
        this.scopeChecking = false;
        this.scopeError =
          err?.status === 400
            ? 'Assign a delegate account before checking scopes.'
            : 'The scope check could not be completed.';
      },
    });
  }

  /** Apply the Google-reported id after an id_mismatch. */
  applyCorrection(): void {
    const actual = this.checkResult?.actualGoogleId;
    if (!(this.customer && actual) || this.correcting) {
      return;
    }
    this.correcting = true;
    this.correctionError = null;
    this.customerService.correctGoogleId(this.customer.id, actual).subscribe({
      next: ({ googleId }) => {
        this.correcting = false;
        this.checkResult = null;
        if (this.customer?.providers[0]) {
          this.customer.providers[0].googleId = googleId;
          this.customer.providers[0].providerUuid = googleId;
        }
      },
      error: (err: HttpErrorResponse) => {
        this.correcting = false;
        this.correctionError =
          err.status === 409
            ? 'That Google ID already belongs to another customer.'
            : 'The correction could not be applied. Please try again.';
      },
    });
  }

  /** Chip tone for an onboarding step status (matches the DWD chip palette). */
  stepStatusTone(status: OnboardingStepStatus): ChipTone {
    switch (status) {
      case 'complete':
        return 'ok';
      case 'incomplete':
        return 'warn';
      default:
        return 'neutral';
    }
  }

  /** Chip tone for a full-asset-sync terminal status. */
  assetSyncTone(status: string | null): ChipTone {
    if (status === ExecutionStatus.finished) return 'ok';
    if (!status) return 'neutral';
    return 'warn';
  }

  /** Friendly label for a full-asset-sync terminal status. */
  assetSyncLabel(status: string | null): string {
    switch (status) {
      case ExecutionStatus.finished:
        return 'Completed';
      case ExecutionStatus.failed:
        return 'Failed';
      case ExecutionStatus.error:
        return 'Error';
      case ExecutionStatus.cancelledRunning:
      case ExecutionStatus.cancelledPending:
        return 'Cancelled';
      default:
        return status ?? 'Unknown';
    }
  }

  stepStatusLabel(status: OnboardingStepStatus): string {
    switch (status) {
      case 'complete':
        return 'Complete';
      case 'incomplete':
        return 'Incomplete';
      default:
        return 'Not tracked';
    }
  }

  backToList(): void {
    this.router.navigate(['/customers']);
  }
}
